import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY = { title: "", slug: "", category: "Personal Finance", excerpt: "", content: "", author: "BharatBest Editorial", image: "", tags: "", status: "published", read_time: "4 min read", seo_title: "", seo_desc: "" };
const CATS = ["Personal Finance", "Personal Loans", "Home Loans", "Business Loans", "Education Loans", "Vehicle Loans", "Loan Against Property", "CIBIL", "Credit Score", "EMI", "Financial Awareness"];

export default function BlogAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/blog").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const open = (b) => { setEditing(b || {}); setForm(b ? { ...b, tags: (b.tags || []).join(", ") } : EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: String(form.tags).split(",").map((t) => t.trim()).filter(Boolean) };
    if (!editing?.id) payload.published_at = new Date().toISOString();
    try {
      if (editing?.id) await api.put(`/admin/blog/${editing.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Article saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Article deleted");
    load();
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Blog / Financial Education</h1>
          <p className="text-sm text-brand-muted mt-0.5">Published articles appear on /blog</p>
        </div>
        <button onClick={() => open(null)} data-testid="add-post-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> New Article</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="post-form">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Title *</label>
            <input required className="input-site mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Slug (blank = auto)</label>
            <input className="input-site mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Category</label>
            <select className="input-site mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="post-category">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Author</label>
            <input className="input-site mt-1" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Status</label>
            <select className="input-site mt-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="post-status">
              <option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
            </select>
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Featured Image URL</label>
            <input className="input-site mt-1" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Excerpt</label>
            <textarea className="input-site mt-1 min-h-[60px]" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-excerpt" />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Content — blank line between paragraphs</label>
            <textarea required className="input-site mt-1 min-h-[200px]" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="post-content" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Tags — comma separated</label>
            <input className="input-site mt-1" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Read Time</label>
            <input className="input-site mt-1" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">SEO Title / Description</label>
            <div className="grid sm:grid-cols-2 gap-3 mt-1">
              <input className="input-site" placeholder="SEO title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
              <input className="input-site" placeholder="Meta description" value={form.seo_desc} onChange={(e) => setForm({ ...form, seo_desc: e.target.value })} />
            </div>
          </div>
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="post-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save Article</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="space-y-3">
          {rows.map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`post-row-${b.slug}`}>
              <div className="min-w-0">
                <p className="font-heading font-bold text-brand-dark truncate">{b.title}</p>
                <p className="text-xs text-brand-muted mt-1">{b.category} · {b.author} · /blog/{b.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${b.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{b.status}</span>
                <button onClick={() => open(b)} data-testid={`post-edit-${b.slug}`} className="text-brand-muted hover:text-brand-dark"><Pencil size={15} /></button>
                <button onClick={() => remove(b.id)} data-testid={`post-delete-${b.slug}`} className="text-brand-muted hover:text-brand-red"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <p className="text-center text-brand-muted text-sm py-10">No articles yet.</p>}
        </div>
      )}
    </div>
  );
}
